import { InboundItem } from "../../types/item";
import { getDb } from "..";
import { captureIndexDBError } from "../sentry-helpers";

export async function saveInboundItem(item: InboundItem) {
    try {
      const db = await getDb();
      await db.put("inboundItems", item);
      return item; // Return the saved item
    } catch (e: any) {
      console.error(`Failed to create item in IndexedDB:`, e);
      captureIndexDBError("saveInboundItem", e, {
        store: "inboundItems",
        key: item.tempId,
        itemId: item.id,
        hasMedia: (item.media?.length || 0) > 0,
      });
      throw e; // Re-throw to let calling code handle the error
    }
  }
  
  export async function getInboundItemsFromDB() {
    try {
      const db = await getDb();
      return await db.getAll("inboundItems");
    } catch (e: any) {
      console.error(`Failed to get items from IndexedDB:`, e);
      captureIndexDBError("getInboundItemsFromDB", e, {
        store: "inboundItems",
      });
      return [];
    }
  }
  
  export async function getInboundItemById(tempId: number) {
    try {
      const db = await getDb();
      return await db.get("inboundItems", tempId as number);
    } catch (e: any) {
      console.error(`Failed to get item by ID from IndexedDB:`, e);
      captureIndexDBError("getInboundItemById", e, {
        store: "inboundItems",
        key: tempId,
      });
      return undefined;
    }
  }
  
  export async function deleteInboundItem(tempId: number) {
    try {
      const db = await getDb();
      await db.delete("inboundItems", tempId);
    } catch (e: any) {
      console.error(`Failed to delete item from IndexedDB:`, e);
      captureIndexDBError("deleteInboundItem", e, {
        store: "inboundItems",
        key: tempId,
      });
      throw e;
    }
  }
  

  export async function updateInboundItemField(
    tempId: string | number, 
    field: keyof InboundItem, 
    value: any
  ) {
    try {
      const db = await getDb();
      
      // Get the existing item by tempId (since it's the keyPath)
      const existingItem = await db.get("inboundItems", tempId as number);
      if (!existingItem) {
        throw new Error(`Item with tempId ${tempId} not found`);
      }
      
      // Update only the specific field
      const updatedItem = {
        ...existingItem,
        [field]: value
      };
      
      // Put back using tempId as the key
      await db.put("inboundItems", updatedItem);
      return updatedItem;
    } catch (e: any) {
      console.error(`Failed to update item field in IndexedDB:`, e);
      captureIndexDBError("updateInboundItemField", e, {
        store: "inboundItems",
        key: tempId,
        field: String(field),
      });
      throw e;
    }
  }

  export async function updateInboundItemFields(
    tempId: number, 
    updates: Partial<InboundItem>
  ) {
    try {
      const db = await getDb();
      const existingItem = await db.get("inboundItems", tempId as number);
      if (!existingItem) {
        throw new Error(`Item with tempId ${tempId} not found`);
      }
      
      // Update multiple fields at once
      const updatedItem = {
        ...existingItem,
        ...updates
      };
      
      await db.put("inboundItems", updatedItem);
      return updatedItem;
    } catch (e: any) {
      console.error(`Failed to update item fields in IndexedDB:`, e);
      captureIndexDBError("updateInboundItemFields", e, {
        store: "inboundItems",
        key: tempId,
        fieldsUpdated: Object.keys(updates),
      });
      throw e;
    }
  }